import { useIsFetching } from '@tanstack/react-query'
import { Link, useRouter, useRouterState, type ErrorComponentProps } from '@tanstack/react-router'
import { faHouse } from '@church/ui-icons'
import { useEffect, useState } from 'react'
import { ErrorState, Icon, ListSkeleton } from './ui.tsx'

/**
 * What a page shows while it loads, when it fails, and when it isn't there.
 * They render inside the shell, so the menu and the top bar stay: a broken
 * page is one page, never the whole app.
 */

/** A page that threw: what broke, that the rest still works, and the ways on. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const [retrying, setRetrying] = useState(false)
  return (
    <ErrorState
      error={error}
      retrying={retrying}
      safe="Only this page is affected: the rest of ForChurch is working, and nothing was changed."
      onRetry={() => {
        setRetrying(true)
        reset()
        void router.invalidate().finally(() => setRetrying(false))
      }}
    >
      <Link
        to="/"
        className="inline-flex items-center gap-2 rounded-control px-button-x py-button-y text-sm font-medium text-content-brand hover:bg-surface-sunken"
      >
        <Icon icon={faHouse} />
        Go to Dashboard
      </Link>
    </ErrorState>
  )
}

/** A page on its way: rows in the list rhythm, not a spinner. */
export function RoutePending() {
  return (
    <div className="mx-auto max-w-5xl overflow-clip rounded-card border border-border-subtle bg-surface-raised">
      <ListSkeleton rows={6} />
    </div>
  )
}

/** An address that leads nowhere, inside the shell. */
export function RouteNotFound() {
  return (
    <div className="mx-auto max-w-md px-6 py-12 text-center">
      <p className="text-4xl font-semibold text-content-default tabular-nums">404</p>
      <h2 className="mt-2 text-lg font-semibold text-content-default">This page doesn’t exist</h2>
      <p className="mt-1 text-sm text-content-muted">
        The link may be old, or what it pointed to was removed.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-2 rounded-control bg-brand-solid px-button-x py-button-y text-sm font-medium text-brand-on-solid hover:bg-brand-solid-hover"
      >
        <Icon icon={faHouse} />
        Go to Dashboard
      </Link>
    </div>
  )
}

/**
 * A thin bar along the top bar's bottom edge while a page loads, or data
 * takes longer than a moment: the app is working, not stuck.
 */
export function RouteProgress() {
  const loading = useRouterState({ select: (s) => s.status === 'pending' })
  const fetching = useIsFetching() > 0
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!fetching) {
      setSlow(false)
      return
    }
    const timer = window.setTimeout(() => setSlow(true), 400)
    return () => window.clearTimeout(timer)
  }, [fetching])
  if (!loading && !slow) return null
  return (
    <div
      role="progressbar"
      aria-label="Loading"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 overflow-hidden"
    >
      <div className="route-progress h-full bg-brand-solid" />
    </div>
  )
}

/** Offline: says so, and what that means for changes, until the connection is back. */
export function ConnectionBanner() {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [back, setBack] = useState(false)
  useEffect(() => {
    const up = () => {
      setOnline(true)
      setBack(true)
      window.setTimeout(() => setBack(false), 3000)
    }
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])
  if (online && !back) return null
  return (
    <div
      role="status"
      className={`border-b border-border-subtle px-4 py-2 text-sm sm:px-6 ${
        online ? 'bg-success-soft text-success-content' : 'bg-warning-soft text-warning-content'
      }`}
    >
      {online
        ? 'Back online. Everything you see is current again.'
        : 'You’re offline. What’s on screen is the last copy saved here; changes won’t save until the connection is back.'}
    </div>
  )
}
